import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'

const nullableString = z.string().nullable().optional()

const participationSchema = z
  .object({
    is_joined: z.boolean().optional(),
    home_state: z.string().optional(),
    joined_count: z.number().optional(),
    pending_count: z.number().optional(),
  })
  .passthrough()

const pouleSummarySchema = z
  .object({
    joined_poules_count: z.number().optional(),
    to_predict_count: z.number().optional(),
    to_predict_state: nullableString,
    matches_today_count: z.number().nullable().optional(),
  })
  .passthrough()

const liveMatchesSummarySchema = z
  .object({
    matches_count: z.number().optional(),
    has_live_matches: z.boolean().optional(),
  })
  .passthrough()

const footballBootstrapSchema = z
  .object({
    poule_summary: pouleSummarySchema.optional(),
    live_matches_summary: liveMatchesSummarySchema.optional(),
  })
  .passthrough()

const gameSchema = z
  .object({
    key: z.string(),
    name: z.string().optional(),
    family: z.string().optional(),
    sort_order: z.number().optional(),
    home_sort_order: z.number().optional(),
    enabled: z.boolean().optional(),
    visible: z.boolean().optional(),
    platform_visibility: z.record(z.string(), z.boolean()).optional(),
    participation: participationSchema.optional(),
    unavailable_reason_html: nullableString,
    bootstrap: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough()

const gameModesResponseSchema = z
  .object({
    requested_platform: z.string().optional(),
    supported_platforms: z.array(z.string()).optional().default([]),
    games: z.array(gameSchema).optional().default([]),
  })
  .passthrough()

export type GameMode = z.infer<typeof gameSchema>
export type GameModeParticipation = z.infer<typeof participationSchema>
export type FootballGameMode = Omit<GameMode, 'bootstrap'> & {
  bootstrap: z.infer<typeof footballBootstrapSchema>
}
export type GameModesResponse = z.infer<typeof gameModesResponseSchema>

async function fetchHomeGameModes(): Promise<GameModesResponse> {
  const params = new URLSearchParams({
    context: 'home',
    platform: 'web',
  })
  const response = await api.get<unknown>(`/app/game-modes?${params.toString()}`)
  const parsed = gameModesResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[game-modes] Invalid home payload', parsed.error.flatten())
    throw new Error('De home kon niet worden geladen.')
  }

  return parsed.data
}

export function useHomeGameModes() {
  return useQuery({
    queryKey: ['home-game-modes', 'web'],
    queryFn: fetchHomeGameModes,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

export function getGameModeByKey(
  response: GameModesResponse | undefined,
  key: string,
): GameMode | undefined {
  const normalizedKey = key.trim().toLowerCase()
  return response?.games.find((game) => game.key.trim().toLowerCase() === normalizedKey)
}

export function getFootballGameMode(
  response: GameModesResponse | undefined,
): FootballGameMode | undefined {
  const game = getGameModeByKey(response, 'football_poule')
  if (!game) return undefined

  const parsedBootstrap = footballBootstrapSchema.safeParse(game.bootstrap)
  return parsedBootstrap.success
    ? { ...game, bootstrap: parsedBootstrap.data }
    : undefined
}
